import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { RepeatButton } from '../../../../../components/repeat-button';

import { uploadOrderDocumentRequest } from '../../../../../store/customs-documents/customs-documents.actions';
import {
  selectCustomsDocumentsOrderDocuments,
  selectCustomsDocumentsIsUploadingOrderDocument,
  selectCustomsDocumentsOrderUploadProgress,
} from '../../../../../store/customs-documents/customs-documents.selectors';
import { DocumentDropZone } from './document-drop-zone';
import { DocumentTypeDropdown } from './document-type-dropdown';
import { OrderDocumentListItem } from './order-document-list-item';
import { messages } from './messages';
import style from './customs-documents-section.module.less';
import type { CustomsDocument } from './types';
import type { UploadFormState } from './document-upload-form';

const ACCEPTED_FILE_TYPES = ['.pdf', '.png'];
const MAX_FILE_SIZE_MB = 10;

type OrderDocumentUploadFormProps = {
  salesOrderId: string;
  availableDocumentTypes: string[];
  initialState?: UploadFormState;
  listOnly?: boolean;
  isDisabled?: boolean;
  onRefresh?: () => void;
};

/**
 * Upload form for order-level (pre-ship) customs documents.
 */
export const OrderDocumentUploadForm = ({
  salesOrderId,
  availableDocumentTypes,
  initialState,
  listOnly,
  isDisabled,
  onRefresh,
}: OrderDocumentUploadFormProps) => {
  const dispatch = useDispatch();
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(null);

  const documents = useSelector((state) => selectCustomsDocumentsOrderDocuments(state, salesOrderId));
  const isUploading = useSelector((state) => selectCustomsDocumentsIsUploadingOrderDocument(state, salesOrderId));
  const uploadProgress = useSelector((state) => selectCustomsDocumentsOrderUploadProgress(state, salesOrderId));

  const computedState = documents.length > 0 ? 'initialized' : 'ready';
  const formState = documents.length === 0 && initialState ? initialState : computedState;
  const showUploadControls = !listOnly && formState !== 'notInitialized';

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!selectedDocumentType) {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.selectTypeBeforeUpload} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        return;
      }
      dispatch(uploadOrderDocumentRequest(salesOrderId, selectedDocumentType, file));
      setSelectedDocumentType(null);
    },
    [dispatch, salesOrderId, selectedDocumentType],
  );

  const handleUploadError = useCallback((errorMessage: string) => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: errorMessage,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  }, []);

  return (
    <FlexColumn className={style['upload-form']} gap={Gap.Space12}>
      {showUploadControls && (
        <>
          <FlexColumn className={style['document-type-wrapper']} gap={Gap.Space4}>
            <DocumentTypeDropdown
              availableTypes={availableDocumentTypes}
              hasDocuments={documents.length > 0}
              isDisabled={isDisabled || isUploading}
              onChange={setSelectedDocumentType}
              selectedType={selectedDocumentType}
            />
          </FlexColumn>
          <DocumentDropZone
            acceptedFileTypes={ACCEPTED_FILE_TYPES}
            isDisabled={isDisabled || isUploading || !selectedDocumentType}
            isUploading={isUploading}
            maxFileSizeMB={MAX_FILE_SIZE_MB}
            onFileSelect={handleFileSelect}
            onUploadError={handleUploadError}
            uploadProgress={uploadProgress}
          />
        </>
      )}

      {documents.length > 0 && (
        <>
          <FlexRow className={style['section-header']} gap={Gap.Space8}>
            <Text formattedMessageValues={{ count: documents.length }} textType={TextType.H4} value={messages.uploadedFilesHeading} />
            {onRefresh && (
              <RepeatButton
                actionHandler={onRefresh}
                ariaLabelMessageDescriptor={messages.refreshAriaLabel}
                className={style['refresh-icon']}
              />
            )}
          </FlexRow>
          <ul className={style['document-list']}>
            {documents.map((doc: CustomsDocument) => (
              <li key={doc.documentId}>
                <OrderDocumentListItem document={doc} salesOrderId={salesOrderId} />
              </li>
            ))}
          </ul>
        </>
      )}
    </FlexColumn>
  );
};
